import { Button, Icon, type IconType } from "@sellar-io/components";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import {
  UNIFIED_CHECKOUT_BASKET_OVERVIEW,
  UNIFIED_CHECKOUT_BASKET_PAY,
} from "../../constants/routes";
import { useUnifiedCheckout } from "./UnifiedCheckoutProvider.shared";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { CheckoutDialog } from "../stockList/checkoutV2/lib/CheckoutDialog";
import { CheckoutEditPhoneNumberForm } from "../stockList/checkoutV2/lib/CheckoutEditPhoneNumberForm";
import { CheckoutCustomCommentForm } from "../stockList/checkoutV2/lib/CheckoutCustomCommentForm";
import { CheckoutEditDraughtDutyForm } from "../stockList/checkoutV2/lib/CheckoutEditDraughtDutyForm";
import { mapToDraughtRequirements } from "../stockList/checkoutV2/lib/mapToDraughtRequirements";
import { CheckoutOrderSummary } from "../stockList/checkoutV2/lib/CheckoutOrderSummary";
import { CheckoutPageLayout } from "../stockList/checkoutV2/lib/CheckoutPageLayout";
import { twMerge } from "tailwind-merge";
import { CheckoutAddress } from "../stockList/checkoutV2/lib/types";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../components/radioSelector/RadioGroup";
import { SellarDistributionContactCard } from "../stockList/checkoutV2/lib/SellarDistributionContactCard";
import dayjs from "dayjs";
import { useUnifiedCheckoutSummaryItems } from "./hooks/useUnifiedCheckoutSummaryItems";
import { useUnifiedCheckoutTracking } from "./useUnifiedCheckoutTracking";
import { UnifiedCheckoutDeliveryDateSummary } from "./components/UnifiedCheckoutDeliveryDateSummary";

// ─── Sub-components ──────────────────────────────────────────────────────────

const InfoRow = ({
  label,
  value,
  icon,
  onEdit,
  editable = true,
}: {
  label: string;
  value: string | ReactNode;
  icon: IconType;
  onEdit: () => void;
  editable?: boolean;
}) => (
  <div
    className={twMerge(
      "group relative flex items-center justify-between rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm",
      editable && "cursor-pointer transition-all hover:shadow-md",
    )}
    onClick={editable ? onEdit : undefined}
  >
    <div className="flex items-center gap-3">
      <Icon icon={icon} size={0.6} className="flex-none text-zinc-500" />
      <div className="flex flex-col">
        <span className="font-semibold">{label}</span>
        <div>{value}</div>
      </div>
    </div>
    {editable && (
      <button
        type="button"
        onClick={onEdit}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-zinc-400 transition-colors group-hover:bg-zinc-100 group-hover:text-zinc-600 hover:bg-zinc-100 hover:text-zinc-600"
      >
        <Icon icon="PENCIL" size={0.6} />
      </button>
    )}
  </div>
);

const TimeSlotCard = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <div
    onClick={onClick}
    className={twMerge(
      "flex flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-3 shadow-sm transition-all hover:shadow-md",
      selected
        ? "border-purple-200 bg-purple-50"
        : "border-zinc-200 bg-white hover:border-zinc-300",
    )}
  >
    <div
      className={twMerge(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
        selected
          ? "border-purple-600 bg-purple-600"
          : "border-zinc-300 bg-white",
      )}
    >
      {selected && <Icon icon="CHECK" size={0.4} className="text-white" />}
    </div>
    <span className="text-sm font-medium text-zinc-900">{label}</span>
  </div>
);

// ─── Details page ────────────────────────────────────────────────────────────

export const UnifiedCheckoutDetailsPage = () => {
  const navigate = useNavigate();
  const { sdBasket, sdOrders, updateBasket, supplierInfoMap } =
    useUnifiedCheckout();
  const { trackStepViewed } = useUnifiedCheckoutTracking();
  const trackDetailsViewed = useEffectEvent(() => trackStepViewed("details"));
  useEffect(() => {
    trackDetailsViewed();
  }, []);
  const { business } = useSellarCurrentBusiness();
  const { orderItemList: allOrderItems, orderSummary } =
    useUnifiedCheckoutSummaryItems();

  const [activeDialog, setActiveDialog] = useState<
    "phone" | "instructions" | "comments" | "draughtDuty" | null
  >(null);
  const [deliveryPreference, setDeliveryPreference] = useState<
    "consolidated" | "split"
  >(sdBasket.deliveryPreference ?? "consolidated");
  const [isSavingDeliveryPreference, setIsSavingDeliveryPreference] =
    useState(false);
  const [deliveryPreferenceError, setDeliveryPreferenceError] = useState("");

  const handleBack = () =>
    navigate(
      UNIFIED_CHECKOUT_BASKET_OVERVIEW.href({ sdBasketUuid: sdBasket.uuid }),
    );

  const handleNext = () =>
    navigate(UNIFIED_CHECKOUT_BASKET_PAY.href({ sdBasketUuid: sdBasket.uuid }));

  const handleContactUpdate = useCallback(
    (id?: number) =>
      updateBasket({
        contactId: id ?? null,
      }),
    [updateBasket],
  );

  const handleDeliveryPreferenceChange = useCallback(
    (value: string) => {
      if (isSavingDeliveryPreference) return;

      const nextPreference = value as "consolidated" | "split";
      const previousPreference = deliveryPreference;
      setDeliveryPreference(nextPreference);
      setDeliveryPreferenceError("");
      setIsSavingDeliveryPreference(true);

      updateBasket({ deliveryPreference: nextPreference })
        .catch(() => {
          setDeliveryPreference(previousPreference);
          setDeliveryPreferenceError(
            "We couldn't save your delivery preference. Please try again.",
          );
        })
        .finally(() => setIsSavingDeliveryPreference(false));
    },
    [deliveryPreference, isSavingDeliveryPreference, updateBasket],
  );

  // ─── Delivery Estimates ───

  // Each draft order retains the delivery date shown for that supplier in
  // search. The basket date is the latest of those dates after reconciliation.
  const consolidatedDate = sdBasket.deliveryDate;
  const isDeliveryUnavailable = !consolidatedDate;

  const splitDates = useMemo(() => {
    const groups = new Map<string, string[]>();
    sdOrders.forEach((order) => {
      if (!order.expectedDeliveryDate) return;
      const date = dayjs(order.expectedDeliveryDate).format("YYYY-MM-DD");
      const supplierName =
        supplierInfoMap.get(order.SupplierId ?? 0)?.supplier.Business.name ??
        "Supplier";
      groups.set(date, [...(groups.get(date) ?? []), supplierName]);
    });
    return [...groups.entries()]
      .map(([date, supplierNames]) => ({ date, supplierNames }))
      .sort((first, second) => first.date.localeCompare(second.date));
  }, [sdOrders, supplierInfoMap]);
  const showSplitDelivery =
    splitDates.length > 1 ||
    (splitDates[0]?.date != null &&
      consolidatedDate != null &&
      splitDates[0].date !== dayjs(consolidatedDate).format("YYYY-MM-DD"));

  // ─── Initial defaults ───
  const currentAddress: CheckoutAddress = sdBasket.deliveryAddress || {
    name: business?.DeliveryAddress.name || "",
    street: business?.DeliveryAddress.street || "",
    city: business?.DeliveryAddress.city || "",
    region: business?.DeliveryAddress.region || "",
    postalCode: business?.DeliveryAddress.postalCode || "",
    country: business?.DeliveryAddress.country || "GB",
  };

  const currentPhone = sdBasket.phoneNumber || business?.phoneNumber || "";
  const hasDraughtDutyOrderItems = allOrderItems.some(
    (item) => item.qualifiesForDraughtDuty,
  );

  // ─── Main content ───
  const mainContent = (
    <div className="flex w-full max-w-242 flex-col gap-8 p-4">
      {/* Delivery Info */}
      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">Your delivery information</h2>
        {isDeliveryUnavailable && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Sellar Distribution is not currently available for this delivery
            address. Choose an eligible address to continue.
          </div>
        )}
        <UnifiedCheckoutDeliveryDateSummary
          deliveryPreference={deliveryPreference}
          consolidatedDeliveryDate={sdBasket.deliveryDate}
          supplierDeliveryDates={sdOrders.map(
            (order) => order.expectedDeliveryDate,
          )}
          includeYear
          className="rounded-2xl border border-purple-200 bg-purple-50 p-3 text-sm text-purple-700"
        />
        <div className="flex flex-col gap-3">
          <InfoRow
            label="Delivery address"
            value={
              <>
                {currentAddress.name},{" "}
                {currentAddress.street && <>{currentAddress.street}, </>}
                {currentAddress.city}, {currentAddress.postalCode}
              </>
            }
            icon="MAP_MARKER"
            onEdit={() => undefined}
            editable={false}
          />
          <InfoRow
            label="Phone number"
            value={currentPhone}
            icon="PHONE"
            onEdit={() => setActiveDialog("phone")}
          />
          <InfoRow
            label="Delivery instructions"
            value={sdBasket.deliveryInstructions || "No instructions provided"}
            icon="FILE_DOCUMENT_OUTLINE"
            onEdit={() => setActiveDialog("instructions")}
          />
          <InfoRow
            label="Order comments"
            value={sdBasket.ordersComment || "No comment"}
            icon="FILE_DOCUMENT_OUTLINE"
            onEdit={() => setActiveDialog("comments")}
          />
          {hasDraughtDutyOrderItems && (
            <InfoRow
              label="Draught duty requirements"
              value={mapToDraughtRequirements(
                sdBasket.hasNoDraughtDutyRelief ? "yes" : "no",
              )}
              icon="GLASS_MUG_VARIANT"
              onEdit={() => setActiveDialog("draughtDuty")}
            />
          )}
        </div>
      </section>

      {/* Delivery Preferences */}
      {sdOrders.length > 1 && (
        <section className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">Your delivery preferences</h2>
          {deliveryPreferenceError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {deliveryPreferenceError}
            </div>
          )}
          <RadioGroup
            value={deliveryPreference}
            onChange={handleDeliveryPreferenceChange}
          >
            <RadioGroupContainer
              className={twMerge(
                "rounded-2xl border-purple-200 shadow-sm hover:shadow-md [&>*:first-child>*]:rounded-t-2xl [&>*:last-child>*]:rounded-b-2xl",
                isSavingDeliveryPreference && "pointer-events-none opacity-70",
              )}
            >
              <RadioGroupOption
                value="consolidated"
                className="items-start p-4"
                checkedClassName="bg-purple-50"
                checkedRadioClassName="bg-purple-600 border-purple-600"
              >
                <div className="flex flex-col">
                  <span className="flex items-center gap-2 font-semibold">
                    <Icon icon="MERGE" size={0.7} className="text-purple-600" />
                    Get everything delivered together
                  </span>
                  <span>
                    Get everything on{" "}
                    <strong>
                      {consolidatedDate
                        ? dayjs(consolidatedDate).format("ddd D MMMM")
                        : "TBC"}
                    </strong>
                  </span>
                </div>
              </RadioGroupOption>
              {showSplitDelivery && (
                <RadioGroupOption
                  value="split"
                  className="items-start p-4"
                  checkedClassName="bg-purple-50"
                  checkedRadioClassName="bg-purple-600 border-purple-600"
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="flex items-center gap-2 font-semibold">
                      <Icon
                        icon="LIGHTNING_BOLT"
                        size={0.7}
                        className="text-purple-600"
                      />
                      Fastest delivery
                    </span>
                    {splitDates.map(({ date, supplierNames }) => (
                      <span key={date}>
                        Get {supplierNames.join(", ")} &rarr;{" "}
                        <strong>{dayjs(date).format("ddd D MMMM")}</strong>
                      </span>
                    ))}
                  </div>
                </RadioGroupOption>
              )}
            </RadioGroupContainer>
          </RadioGroup>
        </section>
      )}

      {/* Delivery Contact */}
      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">Delivery contact</h2>
        <SellarDistributionContactCard
          contactId={sdBasket.contactId ?? undefined}
          className="rounded-2xl border-purple-200 shadow-sm hover:shadow-md [&>*:first-child>*]:rounded-t-2xl [&>*:last-child>*]:rounded-b-2xl"
          checkedClassName="bg-purple-50"
          checkedRadioClassName="border-purple-600 bg-purple-600"
          onContactUpdate={handleContactUpdate}
        />
      </section>

      {/* Time Slot */}
      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-medium">Time slot preference</h2>
        <div className="flex gap-3">
          <TimeSlotCard
            label="AM"
            selected={sdBasket.timeSlotPreference === "AM"}
            onClick={() => updateBasket({ timeSlotPreference: "AM" })}
          />
          <TimeSlotCard
            label="Early PM"
            selected={sdBasket.timeSlotPreference === "PM"}
            onClick={() => updateBasket({ timeSlotPreference: "PM" })}
          />
          <TimeSlotCard
            label="Flexible"
            selected={
              !sdBasket.timeSlotPreference ||
              sdBasket.timeSlotPreference === "flexible"
            }
            onClick={() => updateBasket({ timeSlotPreference: "flexible" })}
          />
        </div>
      </section>

      {/* Next button */}
      <Button
        className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
        onClick={handleNext}
        disabled={isDeliveryUnavailable || isSavingDeliveryPreference}
      >
        NEXT <Icon icon="CHEVRON_RIGHT" size={0.8} />
      </Button>
    </div>
  );

  return (
    <>
      <CheckoutPageLayout
        title="Checkout"
        aside={
          <CheckoutOrderSummary
            orderItemList={allOrderItems}
            outOfStockProductIds={[]}
            productIdsWithPricingChange={[]}
            activeProductIdList={[]}
            orderSummary={orderSummary}
            promotions={[]}
            showSupplierName
          />
        }
        alerts={null}
        onClickLeftIcon={handleBack}
      >
        <div className="flex justify-center">{mainContent}</div>
      </CheckoutPageLayout>

      {/* Dialogs */}
      <CheckoutDialog
        isOpen={activeDialog === "phone"}
        onDismiss={() => setActiveDialog(null)}
        title="Phone number"
        description="Update your contact phone number"
      >
        <CheckoutEditPhoneNumberForm
          initialData={currentPhone}
          onConfirm={(phone) => {
            updateBasket({ phoneNumber: phone });
            setActiveDialog(null);
          }}
        />
      </CheckoutDialog>

      <CheckoutDialog
        isOpen={activeDialog === "draughtDuty"}
        onDismiss={() => setActiveDialog(null)}
        title="Draught duty requirements"
        description=""
      >
        <CheckoutEditDraughtDutyForm
          initialData={sdBasket.hasNoDraughtDutyRelief ? "yes" : "no"}
          onConfirm={(draughtDuty) => {
            updateBasket({ hasNoDraughtDutyRelief: draughtDuty === "yes" });
            setActiveDialog(null);
          }}
        />
      </CheckoutDialog>

      <CheckoutDialog
        isOpen={activeDialog === "instructions"}
        onDismiss={() => setActiveDialog(null)}
        title="Delivery instructions"
        description="Provide specific instructions for the delivery"
      >
        <CheckoutCustomCommentForm
          data={{
            deliveryInstructions: sdBasket.deliveryInstructions || "",
            orderComment: sdBasket.ordersComment || "",
          }}
          initialMode="delivery-instructions"
          onSaveDeliveryInstructions={(instructions) => {
            updateBasket({ deliveryInstructions: instructions });
            setActiveDialog(null);
          }}
          onSaveOrderComment={(comment) => {
            updateBasket({ ordersComment: comment });
            setActiveDialog(null);
          }}
        />
      </CheckoutDialog>

      <CheckoutDialog
        isOpen={activeDialog === "comments"}
        onDismiss={() => setActiveDialog(null)}
        title="Order comments"
        description="Add a comment to your order"
      >
        <CheckoutCustomCommentForm
          data={{
            deliveryInstructions: sdBasket.deliveryInstructions || "",
            orderComment: sdBasket.ordersComment || "",
          }}
          initialMode="order-comments"
          onSaveDeliveryInstructions={(instructions) => {
            updateBasket({ deliveryInstructions: instructions });
            setActiveDialog(null);
          }}
          onSaveOrderComment={(comment) => {
            updateBasket({ ordersComment: comment });
            setActiveDialog(null);
          }}
        />
      </CheckoutDialog>
    </>
  );
};
